import { z } from 'zod';

// Why this exists (XT / data-integrity cleanup): itinerary creation used to
// offer soft-deleted contacts/companies in the customer lookup. That is fixed
// at the source, but itineraries created before the fix still point at deleted
// CRM rows — their customer renders blank and they fall out of `crm.v_customer`.
//
// Those records have since been un-deleted by a one-off restore and tagged with
// the `RESTORED` label (in `crm.contact.label` / `crm.company.label`, both
// `json` string arrays), so their itineraries render again. The worklist keys on
// THAT LABEL, not on `deleted_on` — the restore cleared `deleted_on`, so a
// `deleted_on IS NOT NULL` predicate would match nothing. A row leaves the list
// once its itinerary is re-pointed at a customer without the label.

// `itinerary.contact_id` is POLYMORPHIC — it holds either a `crm.contact.id`
// or a `crm.company.id` (there is no `company_id` column on itinerary; see
// `crm.v_customer`, which unions both with crm_type 1=company / 2=contact).
// The customer kind is therefore resolved by which table the id matches.
export const orphanedCustomerKindValues = ['contact', 'company'] as const;
export const orphanedCustomerKindSchema = z.enum(orphanedCustomerKindValues);
export type OrphanedCustomerKind = z.infer<typeof orphanedCustomerKindSchema>;

// Which record carries the RESTORED label — i.e. what was originally deleted.
// Names are kept as-is (`*_deleted`) so existing `@req` tags and URLs stay valid.
// - `contact_deleted`        → the contact itself was restored
// - `company_deleted`        → the company-as-customer was restored
// - `parent_company_deleted` → the contact was never deleted; its PARENT company
//   was. `v_customer` inner-joins company, so the contact was hidden anyway.
// - `missing`                → `contact_id` matches no row in either table
//   (hard-deleted or bad data). Nothing to un-delete; needs re-pointing.
export const orphanedCustomerReasonValues = [
  'contact_deleted',
  'parent_company_deleted',
  'company_deleted',
  'missing',
] as const;
export const orphanedCustomerReasonSchema = z.enum(
  orphanedCustomerReasonValues
);
export type OrphanedCustomerReason = z.infer<
  typeof orphanedCustomerReasonSchema
>;

export const orphanedCustomerRowSchema = z.object({
  itineraryId: z.uuid(),
  itineraryRef: z.string().nullable(),
  itineraryTitle: z.string().nullable(),
  itineraryCreatedOn: z.string(),
  // Owning consultant (`itinerary.account_id`). Nullable — legacy rows exist
  // with no account, and they must still be listed rather than silently
  // dropped by an inner join.
  consultantId: z.uuid().nullable(),
  consultantName: z.string().nullable(),
  consultantEmail: z.string().nullable(),
  // Resolved customer. `customerId` is the raw polymorphic `contact_id`, so it
  // is always present even when `kind` is null (the `missing` case).
  customerId: z.uuid(),
  customerKind: orphanedCustomerKindSchema.nullable(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  customerDeletedOn: z.string().nullable(),
  // Set only when `customerKind === 'contact'` — the contact's parent company.
  parentCompanyId: z.uuid().nullable(),
  parentCompanyName: z.string().nullable(),
  parentCompanyDeletedOn: z.string().nullable(),
  // `company_type` of whichever company row is the fix target. Surfaced because
  // clearing `deleted_on` is NOT sufficient for the row to return to the
  // customer lookup: `v_customer` also requires `company_type` Customer or Agent.
  companyType: z.string().nullable(),
  reason: orphanedCustomerReasonSchema,
});
export type OrphanedCustomerRow = z.infer<typeof orphanedCustomerRowSchema>;

export const orphanedCustomerSortFields = [
  'itineraryCreatedOn',
  'itineraryRef',
  'consultantName',
  'customerName',
] as const;
export type OrphanedCustomerSortField =
  (typeof orphanedCustomerSortFields)[number];

export const findOrphanedCustomersInput = z.object({
  limit: z.number().int().positive().max(100).default(20),
  offset: z.number().int().min(0).default(0),
  // Free-text over itinerary ref + title.
  itinerarySearch: z.string().trim().min(1).optional(),
  // Free-text over the deleted customer's name/email.
  customerSearch: z.string().trim().min(1).optional(),
  // Multi-select: empty/absent means "any consultant".
  consultantIds: z.array(z.uuid()).optional(),
  // Explicit sentinel for "itineraries with no owning consultant" — distinct
  // from an empty `consultantIds`, which means "any consultant". Combines with
  // `consultantIds` as an OR (those consultants, plus the unowned ones).
  unassignedConsultant: z.boolean().optional(),
  reason: orphanedCustomerReasonSchema.optional(),
  customerKind: orphanedCustomerKindSchema.optional(),
  sortBy: z.enum(orphanedCustomerSortFields).default('itineraryCreatedOn'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type FindOrphanedCustomersInput = z.infer<
  typeof findOrphanedCustomersInput
>;

// Consultants that actually own at least one orphaned itinerary. Returned with
// the page (not from a separate accounts query) so the filter dropdown can only
// offer consultants who genuinely have cleanup work.
export const orphanedCustomerConsultantSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  count: z.number().int().min(0),
});
export type OrphanedCustomerConsultant = z.infer<
  typeof orphanedCustomerConsultantSchema
>;

export const orphanedCustomerSummarySchema = z.object({
  // Per-reason totals across the WHOLE org (unfiltered), so the page can show
  // the shape of the problem regardless of the active filters.
  //
  // EVERY reason key is always present, zero-filled — the GROUP BY only returns
  // rows for reasons that actually occur, so the service back-fills the rest.
  // `z.record` requires all keys of the enum, and the UI indexes this by reason
  // for each of the four; a sparse object fails validation.
  byReason: z.record(orphanedCustomerReasonSchema, z.number().int().min(0)),
  // Distinct CRM records still in use — the "how many contacts/companies do I
  // actually have to deal with" number, which is smaller than the itinerary
  // count because one record can own many itineraries.
  distinctCustomers: z.number().int().min(0),
});
export type OrphanedCustomerSummary = z.infer<
  typeof orphanedCustomerSummarySchema
>;

// Re-point one or more itineraries at a different customer. `customerId` is the
// polymorphic target — a `crm.contact.id` or a `crm.company.id`, matching how
// `itinerary.contact_id` stores it.
export const reassignOrphanedCustomerInput = z.object({
  itineraryIds: z.array(z.uuid()).min(1).max(200),
  customerId: z.uuid(),
});
export type ReassignOrphanedCustomerInput = z.infer<
  typeof reassignOrphanedCustomerInput
>;

export const reassignOrphanedCustomerResultSchema = z.object({
  updated: z.number().int().min(0),
  // Source records left with ZERO live itineraries after the move, whose
  // RESTORED label was therefore dropped. Surfaced so the UI can say "…and 1
  // customer is now clean" rather than leaving the outcome invisible.
  clearedCustomerIds: z.array(z.uuid()),
});
export type ReassignOrphanedCustomerResult = z.infer<
  typeof reassignOrphanedCustomerResultSchema
>;

// 2.1 "leave this CRM": approve a RESTORED record as the legitimate customer.
// Drops the label WITHOUT moving any itinerary — the record keeps its work.
// Separate from `reassign` because this is a human assertion ("I checked, this
// is correct"), not a state derived from the itinerary count.
export const approveOrphanedCustomerInput = z.object({
  customerId: z.uuid(),
});
export type ApproveOrphanedCustomerInput = z.infer<
  typeof approveOrphanedCustomerInput
>;

export const approveOrphanedCustomerResultSchema = z.object({
  clearedCustomerIds: z.array(z.uuid()),
});
export type ApproveOrphanedCustomerResult = z.infer<
  typeof approveOrphanedCustomerResultSchema
>;

// ---------------------------------------------------------------------------
// Step 3 — guarded delete.
//
// Deleting a contact/company that is still some itinerary's customer is what
// created this whole backlog in the first place: `v_customer` filters on
// `deleted_on IS NULL`, so the itinerary's customer silently renders blank.
// So a delete is now BLOCKED while live itineraries reference the record —
// the consultant must first re-point them at another customer.
//
// Scoped to the acting consultant's own itineraries per Geir: "We might
// restrict to those itineraries assigned to the consultant / So he is not
// making decisions for others". `blockedByOthers` carries the count of
// itineraries owned by OTHER consultants, which this user may not move — the
// delete stays blocked until those are handled by their owners.
export const customerItineraryRowSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  // `itinerary.itinerary_ref` — the Optigest-facing reference.
  itineraryRef: z.string().nullable(),
  // Denormalised from `itineraries.itinerary_status`; `color` is a consultant
  // -picked hex, rendered as a tinted chip. Both nullable: `status_id` may point
  // at a row with no label, or (legacy) at nothing at all.
  statusId: z.uuid().nullable(),
  statusLabel: z.string().nullable(),
  statusColor: z.string().nullable(),
  // Whether THIS viewer may move this itinerary. True for their own; also true
  // for everyone else's when the viewer is an admin. Named for the permission,
  // not the ownership, because that is what the UI acts on — an admin sees every
  // row movable. `isOwnedByViewer` below still reports raw ownership so the UI
  // can label a row as someone else's while allowing an admin to move it.
  isMovableByViewer: z.boolean(),
  // Raw ownership: does `itinerary.account_id` match the viewer. Independent of
  // role, so the modal can dim + annotate other people's rows even for an admin.
  isOwnedByViewer: z.boolean(),
});
export type CustomerItineraryRow = z.infer<typeof customerItineraryRowSchema>;

export const findCustomerItinerariesInput = z.object({
  // The polymorphic CRM id being deleted — a contact id or a company id.
  customerId: z.uuid(),
});
export type FindCustomerItinerariesInput = z.infer<
  typeof findCustomerItinerariesInput
>;

export const customerItinerariesSchema = z.object({
  data: z.array(customerItineraryRowSchema),
  // Live itineraries this viewer may re-assign — their own, or ALL of them when
  // the viewer is an admin.
  movableByViewer: z.number().int().min(0),
  // Live itineraries this viewer may NOT move (someone else's, and the viewer is
  // not an admin). Non-zero means the record cannot be fully freed by this user
  // alone; always 0 for an admin.
  blockedByOthers: z.number().int().min(0),
  // Server-derived from `ctx.user.roles`, never client-supplied. Surfaced so the
  // modal can explain WHY it is offering to move other consultants' itineraries.
  isAdminViewer: z.boolean(),
});
export type CustomerItineraries = z.infer<typeof customerItinerariesSchema>;

export const paginatedOrphanedCustomersSchema = z.object({
  data: z.array(orphanedCustomerRowSchema),
  count: z.number().int().min(0),
  countAll: z.number().int().min(0),
  consultants: z.array(orphanedCustomerConsultantSchema),
  summary: orphanedCustomerSummarySchema,
});
export type PaginatedOrphanedCustomers = z.infer<
  typeof paginatedOrphanedCustomersSchema
>;
